import React, { useEffect, useId, useRef } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from '../../hooks/useTranslation';

const TransitionMessage = ({ open, type, currentConsent, totalConsents, customDetails }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isDark = customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const { t } = useTranslation();
	const dialogRef = useRef(null);
	const titleId = useId();
	const subtitleId = useId();

	useEffect(() => {
		if (!open) {
			return undefined;
		}
		const timer = window.setTimeout(() => dialogRef.current?.focus(), 0);
		return () => window.clearTimeout(timer);
	}, [open]);

	if (!open) return null;

	const titleText =
		type === 'approved'
			? t('paymentConsent.messages.sequential.consentApproved')
			: t('paymentConsent.messages.sequential.consentRejected');

	return (
		<Box
			sx={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: 'rgba(0, 0, 0, 0.5)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 9999,
			}}
		>
			<Box
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				aria-describedby={subtitleId}
				tabIndex={-1}
				sx={{
					backgroundColor: isDark ? '#1e1e1e' : '#fff',
					padding: isMobile ? '2rem 1.5rem' : '2rem 2rem',
					borderRadius: customDetails?.borderRadius || '8px',
					boxShadow: isDark
						? '0 8px 32px rgba(0, 0, 0, 0.6)'
						: '0 8px 32px rgba(0, 0, 0, 0.3)',
					width: isMobile ? '90%' : '455px',
					maxWidth: isMobile ? '350px' : '455px',
					height: isMobile ? 'auto' : '243px',
					textAlign: 'center',
					border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e0e0e0',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: isMobile ? '0.5rem' : '8px',
					outline: 'none',
				}}
			>
				{type === 'approved' ? (
					<CheckCircleIcon
						aria-hidden
						sx={{
							fontSize: isMobile ? '3rem' : '4rem',
							color: customDetails?.successColor || '#4caf50',
							mb: 0,
						}}
					/>
				) : (
					<CancelIcon
						aria-hidden
						sx={{
							fontSize: isMobile ? '3rem' : '4rem',
							color: customDetails?.errorColor || '#f44336',
							mb: 0,
						}}
					/>
				)}

				<Typography
					sx={{
						fontSize: isMobile ? '0.75rem' : '0.875rem',
						color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#666',
						mb: 0,
						fontWeight: 400,
					}}
				>
					{t('paymentConsent.messages.sequential.consentStep', {
						current: currentConsent,
						total: totalConsents,
					})}
				</Typography>

				<Typography
					id={titleId}
					sx={{
						fontSize: isMobile ? '1.25rem' : '1.5rem',
						fontWeight: 700,
						color: isDark ? '#fff' : '#000',
						mb: 0,
					}}
				>
					{titleText}
				</Typography>

				<Typography
					id={subtitleId}
					sx={{
						fontSize: isMobile ? '0.875rem' : '1rem',
						color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#666',
						fontWeight: 400,
					}}
				>
					{t('paymentConsent.messages.sequential.reviewNextConsent')}
				</Typography>
			</Box>
		</Box>
	);
};

export default TransitionMessage;
